import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  WifiOff, MapPin, AlertTriangle, Send, X, Info, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { savePendingIncident, generateOfflineId } from '@/utils/offlineStorage';

interface OfflineIncidentFormProps {
  profile: {
    id: string;
    site_id: string | null;
    company_id: string;
    display_name: string;
  };
  onClose: () => void;
  onSubmitSuccess: () => void;
}

const INJURY_SEVERITY_OPTIONS = [
  { value: 'no_injury', label: 'Tidak Ada Cedera', emoji: '✅', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' },
  { value: 'first_aid', label: 'Ringan (P3K)', emoji: '🩹', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  { value: 'medical_treatment', label: 'Sedang (Rawat Medis)', emoji: '🏥', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' },
  { value: 'lost_time', label: 'Berat (Rawat Inap)', emoji: '🚑', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
  { value: 'fatality', label: 'Fatal', emoji: '⚫', color: 'bg-gray-800 text-white dark:bg-gray-900 dark:text-gray-100' },
];

const AREA_STATUS_OPTIONS = [
  { value: 'secured', label: 'Sudah Aman', emoji: '✅', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' },
  { value: 'temporary_control', label: 'Kontrol Sementara', emoji: '🔶', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  { value: 'still_unsafe', label: 'Masih Berbahaya', emoji: '⚠️', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
];

export function OfflineIncidentForm({ 
  profile, 
  onClose, 
  onSubmitSuccess 
}: OfflineIncidentFormProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    location: '',
    description: '',
    incidentDatetime: new Date().toISOString().slice(0, 16), // Default to now
    injurySeverity: 'no_injury',
    areaSafetyStatus: 'secured',
  });

  const handleSubmit = async () => {
    // Validation
    if (!formData.description.trim()) {
      toast({ 
        title: 'Deskripsi diperlukan', 
        description: 'Jelaskan insiden yang terjadi',
        variant: 'destructive' 
      });
      return;
    }

    if (!formData.location.trim()) {
      toast({ 
        title: 'Lokasi diperlukan', 
        description: 'Isi lokasi dimana insiden terjadi',
        variant: 'destructive' 
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const offlineId = generateOfflineId();

      await savePendingIncident({
        id: offlineId,
        data: {
          location: formData.location,
          description: formData.description,
          incident_datetime: formData.incidentDatetime,
          injury_severity: formData.injurySeverity,
          area_safety_status: formData.areaSafetyStatus,
          reported_by: profile.id,
          company_id: profile.company_id,
          site_id: profile.site_id,
        },
        createdAt: new Date().toISOString(),
        retryCount: 0,
      });

      toast({
        title: 'Insiden Disimpan Offline',
        description: 'Akan dikirim otomatis saat koneksi kembali',
      });

      onSubmitSuccess();
    } catch (error) {
      console.error('Failed to save offline incident:', error);
      toast({
        title: 'Gagal Menyimpan',
        description: 'Coba lagi',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm overflow-hidden"
    >
      <div className="h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-red-500 text-white px-4 py-3 shadow-lg z-10 pt-safe shrink-0">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <WifiOff className="h-5 w-5" />
              <div>
                <h1 className="font-semibold">Lapor Insiden (Offline)</h1>
                <p className="text-xs opacity-80">Data akan disinkronkan saat online</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={onClose}
              className="text-white hover:bg-red-600/20"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="max-w-2xl mx-auto px-4 py-6 space-y-6 pb-8">
            {/* Info Banner */}
            <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl border border-blue-200 dark:border-blue-800">
              <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-medium mb-1">Mode Offline - Hanya Teks</p>
                <p className="text-xs opacity-80">
                  Isi form ini untuk melaporkan insiden. Foto dan voice tidak tersedia offline.
                  Laporan akan dikirim otomatis saat koneksi kembali.
                </p>
              </div>
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                Lokasi Insiden *
              </Label>
              <Input
                placeholder="Contoh: Workshop A, Gudang B, Area Welding"
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                className="h-12"
              />
            </div>

            {/* Date/Time */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                Waktu Kejadian
              </Label>
              <Input
                type="datetime-local"
                value={formData.incidentDatetime}
                onChange={(e) => setFormData(prev => ({ ...prev, incidentDatetime: e.target.value }))}
                className="h-12"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                Deskripsi Insiden *
              </Label>
              <Textarea
                placeholder="Jelaskan apa yang terjadi secara detail: siapa yang terlibat, apa yang terjadi, bagaimana kronologinya..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="min-h-[120px]"
              />
            </div>

            {/* Injury Severity */}
            <div className="space-y-3">
              <Label>Tingkat Cedera</Label>
              <div className="grid grid-cols-1 gap-2">
                {INJURY_SEVERITY_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setFormData(prev => ({ ...prev, injurySeverity: option.value }))}
                    className={`p-3 rounded-xl text-left transition-all flex items-center gap-3 ${
                      formData.injurySeverity === option.value
                        ? `${option.color} ring-2 ring-primary shadow-md`
                        : 'bg-muted/50 hover:bg-muted border border-border/50'
                    }`}
                  >
                    <span className="text-xl">{option.emoji}</span>
                    <span className="font-medium">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Area Safety Status */}
            <div className="space-y-3">
              <Label>Status Area Saat Ini</Label>
              <div className="grid grid-cols-2 gap-3">
                {AREA_STATUS_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setFormData(prev => ({ ...prev, areaSafetyStatus: option.value }))}
                    className={`p-4 rounded-xl text-center transition-all ${
                      formData.areaSafetyStatus === option.value
                        ? `${option.color} ring-2 ring-primary shadow-md`
                        : 'bg-muted/50 hover:bg-muted border border-border/50'
                    }`}
                  >
                    <div className="text-2xl mb-1">{option.emoji}</div>
                    <div className="text-sm font-medium">{option.label}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Submit Bar */}
        <div 
          className="shrink-0 px-4 pt-4 bg-background/95 backdrop-blur-xl border-t"
          style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
        >
          <div className="max-w-2xl mx-auto">
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !formData.description || !formData.location}
              className="w-full h-14 text-base font-semibold rounded-full bg-red-600 hover:bg-red-700"
            >
              {isSubmitting ? (
                'Menyimpan...'
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  Simpan Laporan Insiden
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
